import React, { FC, useRef } from 'react';
import { AIcon, AIconSize } from '../../atoms/AIcon/index';
import { AEyebrow } from '../../atoms/AEyebrow/index';
import { ASubheader } from '../../atoms/ASubheader/index';
import { ASubheaderSize } from '../../atoms/ASubheader/ASubheader.types';
import { ABody } from '../../atoms/ABody/index';
import { ABodySize } from '../../atoms/ABody/ABody.types';
import { DEFAULT_OPEN_IN_NEW_TAB_TEXT } from '../../utils/i18nConstants';
import { useTracking } from '../../hooks';
import * as Styled from './OCardMiniIcon.styles';
import type { OCardMiniIconProps } from './OCardMiniIcon.types';
import {
  DEFAULT_ICON_SIZE,
  DEFAULT_SUBHEADER_SIZE,
  DEFAULT_BODY_SIZE,
  toBoolean,
  resolveEnumSize,
  resolveBodyColor,
} from './OCardMiniIcon.utils';

const COMPONENT_NAME = 'o-web-ui-kit-OCardMiniIcon';
const TEST_ID = 'web-ui-kit-OCardMiniIcon';

const TRAILING_ICON_INTERNAL = 'icon-arrow-right';
const TRAILING_ICON_EXTERNAL = 'icon-external-arrow';
const DEFAULT_COMPONENT_ID = 'o-card-mini-icon';

const OCardMiniIconComponent: FC<OCardMiniIconProps> = ({
  mTextBlockBasic,
  aIcon,
  ctaLink,
  externalLink,
  openInANewTabText = DEFAULT_OPEN_IN_NEW_TAB_TEXT,
  trackingProperties,
  className,
  componentId = DEFAULT_COMPONENT_ID,
}) => {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const heading = mTextBlockBasic?.aSubheader?.text;
  const isExternalLink = toBoolean(externalLink);

  useTracking({
    ref: cardRef,
    trackingProperties: trackingProperties ?? undefined,
    isExternal: isExternalLink,
    text: heading ?? '',
  });

  if (!heading || !ctaLink) {
    return null;
  }

  const eyebrowText = mTextBlockBasic?.aEyebrow?.eyebrowText;
  const bodyText = mTextBlockBasic?.aBody?.bodyText;
  const iconName = aIcon?.icon;

  const trailingIcon = isExternalLink ? TRAILING_ICON_EXTERNAL : TRAILING_ICON_INTERNAL;

  const externalLinkProps = isExternalLink
    ? {
        target: '_blank' as const,
        rel: 'noopener noreferrer',
        'aria-label': `${heading} (${openInANewTabText})`,
      }
    : undefined;

  const modifierClasses =
    [className, !iconName && `${componentId}--no-icon`, isExternalLink && `${componentId}--external`]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <Styled.CardLink
      ref={cardRef}
      href={ctaLink}
      className={modifierClasses}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      data-componentid={componentId}
      {...externalLinkProps}
    >
      {iconName && (
        <Styled.LeadingIconWrapper>
          <AIcon icon={iconName} size={resolveEnumSize(aIcon?.size, AIconSize, DEFAULT_ICON_SIZE)} aria-hidden="true" />
        </Styled.LeadingIconWrapper>
      )}

      <Styled.TextBlockWrapper>
        {eyebrowText && (
          <Styled.EyebrowWrapper>
            <AEyebrow eyebrowText={eyebrowText} />
          </Styled.EyebrowWrapper>
        )}
        <Styled.SubheaderWrapper>
          <ASubheader
            text={heading}
            size={resolveEnumSize(mTextBlockBasic?.aSubheader?.size, ASubheaderSize, DEFAULT_SUBHEADER_SIZE)}
          />
        </Styled.SubheaderWrapper>
        {bodyText && (
          <Styled.BodyWrapper>
            <ABody
              bodyText={bodyText}
              size={resolveEnumSize(mTextBlockBasic?.aBody?.size, ABodySize, DEFAULT_BODY_SIZE)}
              color={resolveBodyColor(mTextBlockBasic?.aBody?.color)}
            />
          </Styled.BodyWrapper>
        )}
      </Styled.TextBlockWrapper>

      <Styled.TrailingIconWrapper>
        <AIcon icon={trailingIcon} size={AIconSize.L} aria-hidden="true" />
      </Styled.TrailingIconWrapper>
    </Styled.CardLink>
  );
};

export const OCardMiniIcon = React.memo(OCardMiniIconComponent);

export { TEST_ID as O_CARD_MINI_ICON_TEST_ID };
export type { OCardMiniIconProps };
